// Mastery Insights — parent-facing analytics for the Core Grade Standards
// track: strengths, current struggles, and the remediation paths Pip opened
// (Core lesson -> Get Ready booster skill -> back to Core).
import { useEffect, useState } from "react";
import { ArrowRight, Award, FileDown, LifeBuoy, TrendingUp } from "lucide-react";
import { api } from "../../lib/api";

const Row = ({ item, tone }) => (
  <li className={`flex items-start gap-2 text-xs rounded-lg px-2.5 py-1.5 ${
    tone === "win" ? "bg-emerald-50" : "bg-amber-50"}`}>
    <span className={`mt-0.5 h-2 w-2 rounded-full shrink-0 ${
      tone === "win" ? "bg-emerald-500" : "bg-amber-500"}`} aria-hidden="true" />
    <span>
      <span className="font-bold">{item.objective}</span>
      <span className="text-muted-foreground"> · {item.module} · {item.status}</span>
    </span>
  </li>
);

export const MasteryInsights = () => {
  const [children, setChildren] = useState([]);
  const [childId, setChildId] = useState(null);
  const [data, setData] = useState(null);
  const [compare, setCompare] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/progress-pdf/${childId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "progress-report.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* toast-free fail-soft */ }
    setDownloading(false);
  };

  useEffect(() => {
    api.get("/children").then(({ data: d }) => {
      setChildren(d);
      if (d.length) setChildId(d[0].child_id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!childId) return;
    api.get(`/math-core/${childId}/insights`)
      .then(({ data: d }) => setData(d)).catch(() => setData(null));
    api.get(`/math-core/${childId}/monthly-compare`)
      .then(({ data: d }) => setCompare(d)).catch(() => setCompare(null));
  }, [childId]);

  if (!children.length) return null;
  return (
    <section className="bg-card rounded-2xl border border-border p-5 space-y-4"
      data-testid="mastery-insights">
      <div className="flex items-center gap-2 flex-wrap">
        <TrendingUp className="h-5 w-5 text-violet-600" aria-hidden="true" />
        <h2 className="text-lg font-extrabold font-display">Mastery Insights</h2>
        <div className="ml-auto flex gap-1.5">
          {children.map((c) => (
            <button key={c.child_id} type="button" onClick={() => setChildId(c.child_id)}
              className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${
                childId === c.child_id ? "border-violet-500 bg-violet-50 text-violet-700"
                  : "border-border text-muted-foreground"}`}
              data-testid={`insights-child-${c.child_id}`}>
              {c.display_name}
            </button>
          ))}
        </div>
      </div>
      {!data ? (
        <p className="text-xs text-muted-foreground">No math activity yet.</p>
      ) : (
        <>
        <button type="button" onClick={downloadPdf} disabled={downloading}
          className="text-xs font-bold text-violet-700 bg-violet-50 hover:bg-violet-100 border-2 border-violet-200 rounded-full px-3 py-1.5 inline-flex items-center gap-1.5"
          data-testid="download-progress-pdf">
          <FileDown className="h-3.5 w-3.5" aria-hidden="true" />
          {downloading ? "Preparing…" : "Download progress report (PDF)"}
        </button>
        <div className="grid sm:grid-cols-2 gap-4">
          {compare && (
            <div className="sm:col-span-2 rounded-xl border-2 border-violet-100 bg-violet-50/50 p-3 space-y-2"
              data-testid="monthly-compare">
              <p className="text-xs font-extrabold uppercase tracking-wide text-violet-700">
                This month — before &amp; after ({compare.month})
              </p>
              <p className="text-sm font-extrabold" data-testid="monthly-compare-change">
                {compare.mastery_change.start} <ArrowRight className="h-3.5 w-3.5 inline text-violet-500" aria-hidden="true" /> {compare.mastery_change.now} skills mastered
                {compare.mastery_change.delta > 0 && (
                  <span className="ml-1.5 text-emerald-600">+{compare.mastery_change.delta}</span>)}
              </p>
              <div className="flex flex-wrap gap-2 text-[11px] font-bold">
                <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">
                  {compare.counts.newly_mastered} newly mastered</span>
                <span className="px-2 py-1 rounded-full bg-sky-100 text-sky-700">
                  {compare.counts.improved} improving</span>
                <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                  {compare.counts.needs_support} keep supporting</span>
                <span className="px-2 py-1 rounded-full bg-violet-100 text-violet-700">
                  {compare.boosters_completed} boosters completed</span>
              </div>
              {compare.newly_mastered.length > 0 && (
                <p className="text-[11px] text-muted-foreground">
                  New wins: {compare.newly_mastered.slice(0, 3).map((s) => s.objective).join(" · ")}
                </p>
              )}
            </div>
          )}
          <div>
            <p className="text-xs font-extrabold uppercase tracking-wide text-emerald-700 mb-2">
              <Award className="h-3.5 w-3.5 inline mr-1" aria-hidden="true" />
              Strengths ({data.totals.mastered} mastered · {data.totals.proficient} proficient)
            </p>
            <ul className="space-y-1.5" data-testid="insights-strengths">
              {data.strengths.length === 0 && (
                <li className="text-xs text-muted-foreground">Nothing mastered yet — first wins coming soon!</li>)}
              {data.strengths.map((s) => <Row key={s.lesson_id} item={s} tone="win" />)}
            </ul>
          </div>
          <div>
            <p className="text-xs font-extrabold uppercase tracking-wide text-amber-700 mb-2">
              Working on ({data.totals.working_on})
            </p>
            <ul className="space-y-1.5" data-testid="insights-struggles">
              {data.struggles.length === 0 && (
                <li className="text-xs text-muted-foreground">No sticky spots right now.</li>)}
              {data.struggles.map((s) => <Row key={s.lesson_id} item={s} tone="work" />)}
            </ul>
          </div>
          {data.remediation_paths.length > 0 && (
            <div className="sm:col-span-2">
              <p className="text-xs font-extrabold uppercase tracking-wide text-sky-700 mb-2">
                <LifeBuoy className="h-3.5 w-3.5 inline mr-1" aria-hidden="true" />
                Remediation paths
              </p>
              <ul className="space-y-1.5" data-testid="insights-remediation">
                {data.remediation_paths.map((r) => (
                  <li key={r.core_lesson_id}
                    className="text-xs bg-sky-50 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 flex-wrap">
                    <span className="font-bold">{r.core_objective}</span>
                    <ArrowRight className="h-3 w-3 text-sky-500" aria-hidden="true" />
                    <span>booster: {r.bridge_objective}</span>
                    <span className={`ml-auto px-2 py-0.5 rounded-full font-bold ${
                      r.status === "closed" ? "bg-emerald-100 text-emerald-700"
                        : "bg-amber-100 text-amber-700"}`}>
                      {r.status === "closed" ? "completed" : "in progress"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        </>
      )}
    </section>
  );
};
